import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCampaign } from "@/contexts/CampaignContext";
import { PageContainer } from "@/components/PageContainer";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Upload, X, PlayCircle } from "lucide-react";
import { useToast } from "@/hooks/useToast";
import { uploadFile } from "@/api/media";

interface UploadState {
  fileId: string;
  progress: number;
  isUploading: boolean;
}

export function Creative() {
  const { creativeFiles, setCreativeFiles } = useCampaign();
  const [files, setFiles] = useState<Array<{ name: string; url: string }>>(
    creativeFiles?.files || []
  );
  const [uploadStates, setUploadStates] = useState<Record<string, UploadState>>({});
  const navigate = useNavigate();
  const { toast } = useToast();

  const acceptedTypes = ['.mp4', '.mov', '.mpeg'];

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    event.target.value = '';

    if (files.length >= 4) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Maximum 4 files allowed"
      });
      return;
    }

    if (!acceptedTypes.some(type => file.name.toLowerCase().endsWith(type))) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Only .mp4, .mov, and .mpeg files are allowed"
      });
      return;
    }

    const fileId = Math.random().toString(36).substring(7);
    setUploadStates(prev => ({
      ...prev,
      [fileId]: { fileId, progress: 0, isUploading: true }
    }));

    try {
      const result = await uploadFile(file, (progress) => {
        setUploadStates(prev => ({
          ...prev,
          [fileId]: { ...prev[fileId], progress }
        }));
      });

      // Update both local state and context
      const updatedFiles = [...files, { name: file.name, url: result.url }];
      setFiles(updatedFiles);
      setCreativeFiles({ files: updatedFiles });

      setTimeout(() => {
        setUploadStates(prev => {
          const { [fileId]: _, ...rest } = prev;
          return rest;
        });
      }, 1000);

    } catch (error) {
      toast({
        variant: "destructive",
        title: "Upload Failed",
        description: error.message
      });
      setUploadStates(prev => {
        const { [fileId]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const removeFile = (index: number) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
    setCreativeFiles({ files: updatedFiles });
  };

  const handleSubmit = () => {
    console.log('Submitting creative files:', { files });
    setCreativeFiles({
      files: files.map(f => ({
        name: f.name,
        url: f.url
      }))
    });
    navigate("/create/contact");
  };

  const isUploading = Object.values(uploadStates).some(state => state.isUploading);

  return (
    <PageContainer>
      <CardHeader>
        <CardTitle>Creative Files</CardTitle>
        <CardDescription>Upload your video creative files (up to 4)</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {Object.values(uploadStates).map(state => (
          <div key={state.fileId} className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg w-96">
              <h3 className="mb-4 text-center">Uploading File...</h3>
              <Progress value={state.progress} className="w-full" />
              <p className="mt-2 text-center">{state.progress}%</p>
            </div>
          </div>
        ))}

        {files.length < 4 && (
          <div className="border-2 border-dashed rounded-lg p-6 text-center">
            <input
              type="file"
              accept=".mp4,.mov,.mpeg"
              onChange={handleFileChange}
              className="hidden"
              id="file-upload"
            />
            <label
              htmlFor="file-upload"
              className="cursor-pointer inline-flex items-center justify-center px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90"
            >
              Upload Video ({files.length}/4)
            </label>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          {files.map((file, index) => (
            <div
              key={file.name}
              className="relative p-4 rounded-lg bg-secondary/30"
            >
              <Button
                variant="destructive"
                size="icon"
                className="absolute -right-2 -top-2 z-10"
                onClick={() => removeFile(index)}
              >
                <X className="h-4 w-4" />
              </Button>

              {Object.values(uploadStates).some(state => state.fileId === file.name) ? (
                <div className="space-y-2">
                  <p className="text-sm font-medium">{file.name}</p>
                  <Progress
                    value={uploadStates[file.name]?.progress || 0}
                    className="w-full"
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="relative aspect-video rounded-lg overflow-hidden bg-black">
                    <video
                      src={file.url}
                      className="w-full h-full object-contain"
                      controls
                    />
                  </div>
                  <p className="text-sm font-medium truncate">{file.name}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate("/create/audience")}
        >
          Back
        </Button>
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={files.length === 0 || isUploading}
        >
          Next: Contact Info
        </Button>
      </CardFooter>
    </PageContainer>
  );
}